import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, PencilSimple, Trash, Gift } from '@phosphor-icons/react'
import { toast } from 'sonner'
import type { GameItem } from '@/types'
import { itemTypeLabel } from '@/lib/itemTypes'
import { EditItemDialog } from './EditItemDialog'

interface ItemsTabProps {
  tableId: string
  items: GameItem[]
  isGameMaster: boolean
  onItemsChange: () => void
  onSaveItem: (itemData: Omit<GameItem, 'id'> & { id?: string }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

export function ItemsTab({ items, isGameMaster, onItemsChange, onSaveItem, onDeleteItem }: ItemsTabProps) {
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingItem, setEditingItem] = useState<GameItem | null>(null)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)

  const handleCreateItem = () => {
    setEditingItem(null)
    setShowEditDialog(true)
  }

  const handleEditItem = (item: GameItem) => {
    setEditingItem(item)
    setShowEditDialog(true)
  }

  const handleDeleteItem = async (itemId: string, itemName: string) => {
    if (!confirm(`Tem certeza que deseja excluir o item "${itemName}"?\n\nEsta ação não pode ser desfeita.`)) {
      return
    }

    setIsDeleting(itemId)
    try {
      await onDeleteItem(itemId)
      toast.success(`Item "${itemName}" excluído com sucesso`)
      onItemsChange()
    } catch (error: any) {
      console.error('Erro ao excluir item:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao excluir item'
      toast.error(errorMessage)
    } finally {
      setIsDeleting(null)
    }
  }

  const handleSaveItem = async (itemData: Omit<GameItem, 'id'> & { id?: string }) => {
    await onSaveItem(itemData)
    toast.success(editingItem ? 'Item atualizado com sucesso' : 'Item criado com sucesso')
    onItemsChange()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Itens da Campanha</h3>
          <p className="text-sm text-muted-foreground">
            Catálogo de itens e artefatos que o mestre pode conceder aos personagens
          </p>
        </div>
        {isGameMaster && (
          <Button onClick={handleCreateItem}>
            <Plus size={16} className="mr-2" />
            Novo Item
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Gift size={32} className="text-muted-foreground" />
            </div>
            <h4 className="text-lg font-medium mb-2">Nenhum item configurado</h4>
            <p className="text-muted-foreground mb-4 max-w-md">
              {isGameMaster
                ? 'Crie itens e artefatos para sua campanha. Você poderá concedê-los aos personagens durante o jogo.'
                : 'O mestre ainda não configurou itens para esta campanha.'}
            </p>
            {isGameMaster && (
              <Button onClick={handleCreateItem}>
                <Plus size={16} className="mr-2" />
                Criar Primeiro Item
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <Card key={item.id} className="group hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{item.icon}</div>
                    <div>
                      <CardTitle className="text-lg">{item.name}</CardTitle>
                      <Badge variant="secondary" className="mt-1">
                        {itemTypeLabel(item.item_type)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <CardDescription className="line-clamp-3">
                  {item.description}
                </CardDescription>

                {isGameMaster && (
                  <div className="flex gap-2 pt-2 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleEditItem(item)}
                    >
                      <PencilSimple size={14} className="mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteItem(item.id, item.name)}
                      disabled={isDeleting === item.id}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EditItemDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        itemData={editingItem}
        onSave={handleSaveItem}
      />
    </>
  )
}
